import { useEffect } from 'react'

import 'src/lib/fragments' // This must be imported so that fragments are registered before queries use them
import { ApolloLink } from '@apollo/client'
import { ConfigProvider } from 'antd'
import useNotification from 'antd/es/notification/useNotification'
import { configure as configureMobX } from 'mobx'
import { ThemeProvider } from 'styled-components'

import { Helmet, RedwoodProvider } from '@cedarjs/web'
import { GraphQLClientConfigProp, RedwoodApolloProvider } from '@cedarjs/web/apollo'

import FatalErrorBoundary from 'src/components/FatalErrorBoundary/FatalErrorBoundary'
import possibleTypes from 'src/graphql/possibleTypes'
import { GWPostHogProvider } from 'src/gw-posthog-provider'
import { ROUTE_ANNOUNCER_ID } from 'src/hooks/use-route-announcement'
import FatalErrorPage from 'src/pages/FatalErrorPage'
import Routes from 'src/Routes'
import { getFeatureFlagOverridesSnapshot } from 'src/utils/featureFlagging/featureFlagOverrides'

import { AuthProvider, useAuth } from './auth'
import { NotificationContext } from './components/shared/Notification/NotificationContext'
import { isLocal } from './constants'
import { AntDesignButtonOverride, AntDesignThemeOverride, Theme } from './constants/theme'
import { GovWellEvervaultProvider } from './utils/foreignStorage'
import './index.css'
import './scaffold.css'
import './tailwind.css'
import { SessionStorageKey } from './types'
import { QueryClientProvider } from './utils/queryClient'
import { GovWellRequestHeaders } from './utils/requestHeaders'

configureMobX({
  computedRequiresReaction: false,
  observableRequiresReaction: false,
  reactionRequiresObservable: false,
})

// Forward the active org from the per-tab session storage so backend
// resolveOrgId() picks up the correct org without every mutation needing to
// pass it explicitly. Read fresh per request since the user can switch orgs.
const activeOrgLink = new ApolloLink((operation, forward) => {
  const raw = sessionStorage.getItem(SessionStorageKey.ActiveOrgId)
  if (raw) {
    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
      headers: {
        ...headers,
        [GovWellRequestHeaders.ActiveOrgId]: raw,
      },
    }))
  }
  return forward(operation)
})

// Local-only: forward the dev panel's feature flag overrides to the backend so
// request-scoped backend gate checks reflect the same toggles as the frontend.
// Read fresh per request since overrides change at runtime as the dev toggles.
const flagOverridesLink = new ApolloLink((operation, forward) => {
  if (isLocal()) {
    const overrides = getFeatureFlagOverridesSnapshot()
    if (Object.keys(overrides).length > 0) {
      operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
        headers: {
          ...headers,
          [GovWellRequestHeaders.FlagOverrides]: JSON.stringify(overrides),
        },
      }))
    }
  }
  return forward(operation)
})

const graphQLClientConfig: GraphQLClientConfigProp = {
  httpLinkConfig: {
    headers: {
      [GovWellRequestHeaders.Platform]: 'Web',
      [GovWellRequestHeaders.TimeZone]: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  },
  link: (redwoodApolloLinks) =>
    ApolloLink.from([
      activeOrgLink,
      flagOverridesLink,
      ...redwoodApolloLinks.map(({ link }) => link),
    ]),
  cacheConfig: {
    possibleTypes: possibleTypes.possibleTypes,
  },
}

const App = () => {
  useEffect(() => {
    const loader = document.getElementById('loader')
    if (loader) {
      loader.style.display = 'none'
    }
  }, [])

  const [notification, contextHolder] = useNotification()

  return (
    <GWPostHogProvider>
      <RedwoodProvider titleTemplate="%PageTitle | %AppTitle">
        <AuthProvider>
          <RedwoodApolloProvider useAuth={useAuth} graphQLClientConfig={graphQLClientConfig}>
            <ThemeProvider theme={Theme}>
              <ConfigProvider theme={AntDesignThemeOverride} button={AntDesignButtonOverride}>
                <FatalErrorBoundary page={FatalErrorPage}>
                  <Helmet>
                    <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.10.377/pdf.js"></script>
                    <script src="https://js.finix.com/v/1/2/3/finix.js"></script>
                    <script src="https://js.paymentus.com/sdk/1.2.3/paymentus_sdk.min.js"></script>
                    <script src="https://js.paymentus.com/sdk/1.2.3/guest_checkout.min.js"></script>
                  </Helmet>
                  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css" />
                  <NotificationContext.Provider value={{ notification }}>
                    <QueryClientProvider>
                      <GovWellEvervaultProvider>
                        {contextHolder}
                        <div
                          id={ROUTE_ANNOUNCER_ID}
                          role="status"
                          aria-live="polite"
                          aria-atomic="true"
                          className="sr-only"
                        />
                        <Routes />
                      </GovWellEvervaultProvider>
                    </QueryClientProvider>
                  </NotificationContext.Provider>
                </FatalErrorBoundary>
              </ConfigProvider>
            </ThemeProvider>
          </RedwoodApolloProvider>
        </AuthProvider>
      </RedwoodProvider>
    </GWPostHogProvider>
  )
}

export default App
